import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import TextInput from '../TextInput';
import { CustomerContext } from '../../contexts/CustomerContext';
import Button from '../Button';
import styles from './AccessCodeOnlyForm.module.scss';
import useQueryString from '../../hooks/use-query-string';
import { routes } from '../../routes';
import { Student, StudentImage, School } from '../../models';
import AffirmationText from './AffirmationText';
import SchoolInput from '../SchoolInput';
import useSchools from '../../hooks/useSchools';

export function isValidEmail(email) {
  // Regular expression pattern for email validation
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
}

export default function AccessCodeOnlyForm() {
  const [accessCode, setAccessCode] = useState('');
  const { customer, setCustomer } = useContext(CustomerContext);
  const { order } = useQueryString();
  const [student, setStudent] = useState();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [packageId, setPackageId] = useState();
  const [studentImageSeason, setStudentImageSeason] = useState('');
  const history = useHistory();
  const schools = useSchools();
  const [email, setEmail] = useState(customer.email || '');
  const [noEmail, setNoEmail] = useState(false);

  const isYearbookOrder = order === 'yearbook_order';

  const fetchStudentData = (stuAccessCode, schoolId) => {
    let createdLocation;
    let parsedAccessCode;
    let isMultipleSeasonPackage;

    if (stuAccessCode.toUpperCase().includes('S-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('S-')[1];
      createdLocation = 'sitting_cards';
    } else if (stuAccessCode.toUpperCase().includes('P-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('P-')[1];
      createdLocation = 'proofs';
    } else if (stuAccessCode.toUpperCase().includes('M-')) {
      isMultipleSeasonPackage = true;
      parsedAccessCode = stuAccessCode.toUpperCase().split('M-')[1];
    } else {
      parsedAccessCode = stuAccessCode;
    }

    if (createdLocation) {
      customer.cart.createdLocation = createdLocation;
      customer.cart.save();
    }

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);
    const selectExtras = ['packageId', isMultipleSeasonPackage && 'multiSeasonPackageId'].filter(n => n)

    StudentImage
      .selectExtra(selectExtras)
      .includes('student')
      .where({
        forAccessCodeAndSchool: [parsedAccessCode, schoolId],
        hasImagePresent: schoolId,
      })
      .merge({ student: studentScope })
      .all()
      .then((res) => {
        if (res.data.length === 0) return;

        const studentImage = res.data[0];

        if (isMultipleSeasonPackage && studentImage.multiSeasonPackageId) {
          setPackageId(studentImage.multiSeasonPackageId);
        } else {
          setPackageId(studentImage.packageId);
        }
        setStudent(studentImage.student);
        setStudentImageSeason(studentImage.folder);
        localStorage.setItem(`currentSeason-${studentImage.student.id}`, studentImage.folder);
      })
      .catch((error) => {
        console.error('Error fetching student data:', error);
      })
      .finally(() => {
        setHasSubmitted(true);
        setNoEmail(false);
      });
  };

  const handleSchoolChange = (opt) => {
    const newSchool = schools.find((school) => school.id === opt.value);
    setCustomer({ ...customer, currentSchool: newSchool });
  };

  useEffect(() => {
    setHasSubmitted(false);
  }, [accessCode]);

  const handleContinue = (e) => {
    e.preventDefault();

    setCustomer({ ...customer, currentStudent: student });
    localStorage.setItem('isUsingAccessCode', 'true');

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: student.studentId, sch: customer.currentSchool.id }));
    } else if (!packageId) {
      history.push('/past_images');
    } else {
      history.push(routes.packageSelection({ pid: packageId, senior: studentImageSeason.toLowerCase().includes('senior') }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!accessCode) return;
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      return;
    }

    setCustomer({ ...customer, email });

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);

    if (accessCode.toUpperCase().includes('C-')) {
      const parsedAccessCode = accessCode.toUpperCase().split('C-')[1]
      StudentImage
        .selectExtra(['packageId'])
        .includes('student')
        .where({ forAccessCodeAndSchool: [parsedAccessCode, customer.currentSchool.id] })
        .merge({ student: studentScope })
        .all()
        .then((res) => {
          if (res.data.length === 0) return;

          history.push(`/landing/composites?sid=${res.data[0].student.studentId}&sch=${customer.currentSchool.id}&access_code=${parsedAccessCode}`)
        })
        .finally(() => {
          setHasSubmitted(true);
          setNoEmail(false);
        });
    } else {
      fetchStudentData(accessCode, customer.currentSchool.id);
    }
  };

  if (student && hasSubmitted) {
    return (
      <div className={styles.form}>
        <AffirmationText handleContinue={handleContinue} student={student} />
      </div>
    );
  }

  const orderText = order === 'senior' ? 'Senior Pictures' : 'Pictures';

  return (
    <>
      <h1 className={styles.title}>{`Purchase ${orderText}`}</h1>
      {noEmail && (
        <p className={styles.noStudentMsg}>
          Please enter a valid email before continuing.
        </p>
      )}
      {hasSubmitted && !student && (
        <p className={styles.noStudentMsg}>
          We were unable to find your student or your images may not be ready yet.
          <br />
          Please come back again later to see if there are any updates.
        </p>
      )}
      <form className={styles.form} onSubmit={handleSubmit}>
        <label style={{ width: '100%', maxWidth: '350px' }}>
          Email
          <TextInput val={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <div style={{ maxWidth: '350px' }}>
          <SchoolInput onChange={handleSchoolChange} />
        </div>
        <label style={{ width: '100%', maxWidth: '350px' }}>
          Access Code
          <TextInput val={accessCode} onChange={(e) => setAccessCode(e.target.value)} />
        </label>
        <Button rounded variant="submit" type="submit" style={{ width: '100%', maxWidth: '350px' }} disabled={!accessCode || !customer?.currentSchool}>
          Submit
        </Button>
      </form>
      <div className={styles.btnContainer}>
        <Link to={routes.getAccessCode()}>
          <Button fullWidth type="button" variant="primary" style={{ width: '100%', maxWidth: '350px' }}>
            Need an Access Code? - Click Here
          </Button>
        </Link>
      </div>
    </>
  );
}
